import { useMutation } from '@tanstack/react-query'
import { ImageUp, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'

import { ApiException, apiFetch } from '../../api/client'
import type { Branding } from '../../theme'
import { Banner, Button, ICON_STROKE, cx } from '../../ui'

interface Props {
  branding: Branding | undefined
  studioName: string
  /** Relee el tenant: el logo forma parte de la sesión, no de esta pantalla. */
  onChanged: () => Promise<void>
}

/**
 * El logo del estudio.
 *
 * Es la otra mitad de la marca blanca. El color ya se elegía; el logo se leía
 * en tres pantallas —panel, entrada y galería del cliente— y no había forma de
 * subirlo, así que el monograma con la inicial era lo único que veía nadie.
 *
 * No usa el uploader de `src/upload/`: eso es para archivos de 50 GB con
 * reanudación, y aquí hablamos de 2 MB que además el servidor tiene que mirar
 * antes de guardar (ver `TenantLogoController`). Un `FormData` y ya.
 */
export function LogoField({ branding, studioName, onChanged }: Props) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [failure, setFailure] = useState<string | null>(null)
  const logo = branding?.logo_url

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const body = new FormData()
      body.append('logo', file)

      // Sin `Content-Type`: lo pone el navegador con el `boundary` del
      // multipart. Fijarlo a mano rompe la petición de una forma que sólo se
      // ve en el servidor, como un cuerpo vacío.
      return apiFetch<{ data: Branding }>('/tenant/logo', { method: 'POST', body })
    },
    onSuccess: async () => {
      setFailure(null)
      await onChanged()
    },
    onError: (error) => {
      setFailure(
        error instanceof ApiException
          ? error.message
          : 'No se pudo subir el logo. Revisa tu conexión.',
      )
    },
  })

  const remove = useMutation({
    mutationFn: () => apiFetch<{ data: Branding }>('/tenant/logo', { method: 'DELETE' }),
    onSuccess: async () => {
      setFailure(null)
      await onChanged()
    },
    onError: () => setFailure('No se pudo quitar el logo.'),
  })

  const busy = upload.isPending || remove.isPending

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-subhead font-semibold text-ink">Tu logo</h2>
        <p className="mt-1 max-w-prose text-body text-ink-muted">
          Va en la cabecera de cada galería que compartes y en tu panel. Si no
          subes ninguno, usamos la inicial de tu estudio.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        {/*
          Fondo hundido tras el logo: la mayoría son PNG con transparencia
          pensados para papel blanco, y sobre el papel del panel desaparecerían
          los que sean claros. Así se ve dónde acaba la imagen.
        */}
        <div className="grid h-20 w-32 shrink-0 place-items-center overflow-hidden rounded-card border border-line bg-surface-sunken">
          {logo !== undefined ? (
            <img src={logo} alt={studioName} className="max-h-16 max-w-28 object-contain" />
          ) : (
            <span
              aria-hidden
              className="grid h-11 w-11 place-items-center rounded-control bg-accent text-heading font-semibold text-accent-contrast"
            >
              {studioName.trim().charAt(0).toUpperCase()}
            </span>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          <input
            ref={inputRef}
            type="file"
            // Sin SVG: el servidor lo rechaza porque puede traer script dentro,
            // y ofrecerlo aquí sería mandar al estudio a un error evitable.
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) upload.mutate(file)
              // Permite volver a elegir el mismo archivo tras un fallo.
              event.target.value = ''
            }}
          />

          <Button
            variant="secondary"
            disabled={busy}
            busy={upload.isPending && 'Subiendo…'}
            onClick={() => inputRef.current?.click()}
          >
            <ImageUp size={16} strokeWidth={ICON_STROKE} aria-hidden />
            {logo === undefined ? 'Subir logo' : 'Cambiar'}
          </Button>

          {logo !== undefined && (
            <Button
              variant="ghost"
              disabled={busy}
              busy={remove.isPending && 'Quitando…'}
              onClick={() => remove.mutate()}
            >
              <Trash2 size={16} strokeWidth={ICON_STROKE} aria-hidden />
              Quitar
            </Button>
          )}
        </div>
      </div>

      <p className={cx('text-caption text-ink-subtle', failure !== null && 'sr-only')}>
        PNG, JPG o WebP, hasta 2 MB. Un PNG con fondo transparente es lo que
        mejor queda sobre cualquier tema.
      </p>

      {failure !== null && (
        <Banner tone="danger" title="No se pudo guardar el logo" live="alert">
          {failure}
        </Banner>
      )}
    </div>
  )
}
